import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Copy, Loader2, Send, Gift, ArrowRight, Sparkles, Trash2, Check,
} from "lucide-react";

/*  STREAMER OUTREACH KIT + COMP-A-CREW TOOL — the "find your first crew"
    page. DM templates + a lightweight comp-pack flow (self-serve for
    approved streamers, 3/day cap). Admin can use the same tool via the
    Admin page.

    Access rules:
      • Public can read the templates (sales page utility).
      • Only approved streamer codes can CREATE comps (self-serve).
      • Admin can create comps from Admin.jsx with the admin token.
*/
const StreamerOutreach = () => {
  const [params] = useSearchParams();
  const code = (params.get("code") || "").toUpperCase();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [comps, setComps]     = useState(null);
  const [packOptions, setPackOptions] = useState([]);

  const [form, setForm] = useState({
    pack_id: "",
    recipient_email: "",
    recipient_name: "",
    note: "",
  });
  const [sending, setSending] = useState(false);
  const [lastComp, setLastComp] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get("/streamer/outreach-kit" + (code ? `?code=${encodeURIComponent(code)}` : ""))
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load the outreach kit."))
      .finally(() => setLoading(false));
  }, [code]);

  useEffect(() => {
    api.get("/packs")
      .then((r) => {
        const opts = (r.data?.packs || []).map((p) => ({ id: p.id, label: p.label }));
        setPackOptions(opts);
        setForm((f) => ({ ...f, pack_id: f.pack_id || (opts[0]?.id || "") }));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!code || code === "YOURCODE") return;
    api.get(`/streamer/${encodeURIComponent(code)}/comps`)
      .then((r) => setComps(r.data))
      .catch(() => setComps(null));
  }, [code, lastComp]);

  const copy = async (text, label = "Copied.") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error("Copy blocked by this browser.");
    }
  };

  const submitComp = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (!code || code === "YOURCODE") {
      toast.error("Add ?code=YOURCODE to this URL first.");
      return;
    }
    setSending(true);
    try {
      const payload = { ...form, streamer_code: code };
      const { data: comp } = await api.post("/comp-crew", payload);
      setLastComp(comp);
      setForm({ pack_id: form.pack_id, recipient_email: "", recipient_name: "", note: "" });
      toast.success("Comp sent. Share the link with your crew.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create the comp.");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="grain min-h-screen bg-[#0a0908] flex items-center justify-center text-[#a39797]">
        <Loader2 size={16} className="animate-spin" /> &nbsp; Loading your outreach kit…
      </div>
    );
  }
  if (!data) return null;

  const hasCode = data.code && data.code !== "YOURCODE";

  return (
    <div className="grain min-h-screen bg-[#0a0908] text-[#f4f0ec]">
      <div className="border-b border-[#2a2525] px-5 py-4 flex items-center justify-between">
        <Link to="/" className="text-[10px] tracking-[0.3em] text-[#d4af37]" data-testid="outreach-logo">
          ◉ WASNTME.PARTY
        </Link>
        <div className="text-[9px] tracking-[0.3em] text-[#5a5050]">STREAMER OUTREACH KIT</div>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-10">
        <h1 className="font-display text-4xl sm:text-5xl mb-3 leading-[1.05]">
          Cold DMs that <span className="text-[#d4af37]">actually land</span>.
        </h1>
        <p className="text-sm text-[#a39797] mb-8 leading-relaxed max-w-2xl">
          Six battle-tested templates + a comp-a-pack tool. Pick a template,
          personalize the first line, send. Hit "Comp a Pack" to send a free crew pack
          to a prospect — fastest-converting opener we've got.
        </p>

        {/* Comp a Crew Pack — only if a valid streamer code */}
        <div className="card-noir p-5 mb-9" data-testid="outreach-comp">
          <div className="flex items-center gap-2 mb-3">
            <Gift size={14} className="text-[#d4af37]" />
            <div className="text-[10px] tracking-[0.3em] text-[#d4af37]">COMP A CREW PACK</div>
            {comps && (
              <div className="ml-auto text-[10px] text-[#a39797]" data-testid="outreach-comp-remaining">
                {comps.remaining_today}/{comps.daily_cap} left today
              </div>
            )}
          </div>
          {!hasCode ? (
            <div className="text-xs text-[#a39797]">
              You need an affiliate code to comp a pack.{" "}
              <Link to="/streamer" className="text-[#d4af37] underline">Apply for one →</Link>
            </div>
          ) : (
            <>
              <form onSubmit={submitComp} className="space-y-3" data-testid="outreach-comp-form">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] tracking-[0.2em] text-[#a39797] block mb-1">PACK</label>
                    <select
                      className="input-noir w-full"
                      value={form.pack_id}
                      onChange={(e) => setForm((f) => ({ ...f, pack_id: e.target.value }))}
                      data-testid="outreach-comp-pack"
                    >
                      {packOptions.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.2em] text-[#a39797] block mb-1">RECIPIENT EMAIL</label>
                    <input
                      type="email" required
                      className="input-noir w-full"
                      placeholder="their@email.com"
                      value={form.recipient_email}
                      onChange={(e) => setForm((f) => ({ ...f, recipient_email: e.target.value }))}
                      data-testid="outreach-comp-email"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] text-[#a39797] block mb-1">RECIPIENT NAME (OPTIONAL)</label>
                  <input
                    type="text" maxLength={80}
                    className="input-noir w-full"
                    placeholder="@theirhandle"
                    value={form.recipient_name}
                    onChange={(e) => setForm((f) => ({ ...f, recipient_name: e.target.value }))}
                    data-testid="outreach-comp-name"
                  />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] text-[#a39797] block mb-1">PERSONAL NOTE (OPTIONAL)</label>
                  <textarea
                    maxLength={400} rows={2}
                    className="input-noir w-full"
                    placeholder="Loved your last stream — run this one, I think your chat will go wild."
                    value={form.note}
                    onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                    data-testid="outreach-comp-note"
                  />
                </div>
                <button type="submit" disabled={sending || (comps && comps.remaining_today <= 0)}
                  className="btn-primary text-sm" data-testid="outreach-comp-submit">
                  {sending ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                  {sending ? "Minting…" : "SEND THE COMP"}
                </button>
                {comps && comps.remaining_today <= 0 && (
                  <div className="text-[10px] text-[#8b0000]">
                    Daily cap hit. Resets at 00:00 UTC.
                  </div>
                )}
              </form>

              {lastComp && (
                <div className="mt-4 rounded-md border border-[#d4af37]/40 bg-[#1a1515] p-3" data-testid="outreach-comp-success">
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] text-[#d4af37] mb-2">
                    <Check size={12} /> COMP READY · SHARE THIS LINK
                  </div>
                  <div className="font-mono text-[11px] text-[#d4d0cc] break-all mb-2">{lastComp.redeem_url}</div>
                  <button onClick={() => copy(lastComp.redeem_url, "Comp URL copied.")}
                    className="btn-ghost text-xs" data-testid="outreach-comp-copy">
                    <Copy size={11} /> Copy
                  </button>
                </div>
              )}

              {comps && comps.comps.length > 0 && (
                <div className="mt-5 border-t border-[#2a2525] pt-4" data-testid="outreach-comp-history">
                  <div className="text-[10px] tracking-[0.3em] text-[#a39797] mb-2">YOUR RECENT COMPS</div>
                  <div className="space-y-1.5">
                    {comps.comps.slice(0, 8).map((c) => (
                      <div key={c.comp_token} className="flex items-center justify-between gap-2 text-[11px]">
                        <div className="text-[#d4d0cc] truncate">
                          {c.pack_label} → <span className="text-[#a39797]">{c.recipient}</span>
                        </div>
                        <div className="shrink-0">
                          {c.redeemed
                            ? <span className="text-[#d4af37]">CLAIMED</span>
                            : <button onClick={() => copy(c.redeem_url, "Comp URL copied.")}
                                className="text-[#a39797] hover:text-[#d4af37]"
                                data-testid={`outreach-comp-copy-${c.comp_token}`}>
                                <Copy size={10} className="inline mr-1" /> Copy link
                              </button>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* DM templates */}
        <div className="space-y-4" data-testid="outreach-templates">
          {data.templates.map((t, i) => (
            <div key={t.id} className="card-noir p-5" data-testid={`outreach-template-${t.id}`}>
              <div className="flex items-center justify-between mb-2 gap-2">
                <div className="text-[11px] tracking-[0.2em] text-[#d4af37]">{t.label}</div>
                <button onClick={() => copy(`Subject: ${t.subject}\n\n${t.body}`, "Template copied.")}
                  className="btn-ghost text-xs" data-testid={`outreach-template-copy-${i}`}>
                  <Copy size={11} /> Copy full DM
                </button>
              </div>
              <div className="text-[10px] tracking-[0.2em] text-[#a39797] mb-1">SUBJECT</div>
              <div className="text-xs text-[#d4d0cc] mb-3">{t.subject}</div>
              <div className="text-[10px] tracking-[0.2em] text-[#a39797] mb-1">BODY</div>
              <div className="text-xs text-[#d4d0cc] whitespace-pre-wrap leading-relaxed">{t.body}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-2">
          <Link to={`/streamer/playbook${hasCode ? `?code=${data.code}` : ""}`}
            className="btn-primary text-sm" data-testid="outreach-cta-playbook">
            Get the go-live playbook <ArrowRight size={12} />
          </Link>
          <Link to="/streamer/dashboard" className="btn-ghost text-xs" data-testid="outreach-cta-dashboard">
            Your dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default StreamerOutreach;
